import { fetchAuthSession } from "aws-amplify/auth";
import { getUrl, uploadData } from "aws-amplify/storage";
import mixpanel from "mixpanel-browser";
import { AudioDatabase, onSaveBlobToIndexDB } from "@/utils/utils";
import useCreateRecording from "./useCreateRecording";

const useUploadRecording = () => {
  const { handleCreateRecording, pollForS3TriggerRecord } = useCreateRecording();
  
  const handleUploadRecording = async (
    recordingItem: {
      name: string;
      caseId: string | null; // Allow null for QuickRecord recordings
      duration: number;
      storagePath: string;
      blob: Blob;
      useTemplate: boolean;
      templateId?: string;
      urlAudio?: string;
    },
    retentionPeriod: number | null,
  ) => {
    try {
      const retention = retentionPeriod === null ? 0 : retentionPeriod;
      const identityId = (await fetchAuthSession()).identityId;
      const s3Put = uploadData({
        path: `private/${identityId}/${recordingItem.storagePath}`,
        data: recordingItem.blob,
        options: {
          metadata: {
            duration: recordingItem.duration.toString(),
            usetemplate: recordingItem.useTemplate.toString(),
            name: recordingItem.name,
            templateid: recordingItem.templateId || '',
            retention: retention.toString(),
            usequickrecord: recordingItem.caseId ? 'false' : 'true',
          },
        },
      });
      await s3Put.result;
      const urlAudio = (
        await getUrl({
          path: `private/${identityId}/${recordingItem.storagePath}`,
          options: { validateObjectExistence: true },
        })
      ).url.toString();
      recordingItem["urlAudio"] = urlAudio;
      onSaveBlobToIndexDB(
        { ...recordingItem, failedToUpload: false },
        recordingItem.caseId 
          ? `${AudioDatabase.PREFIX_RECORD}${recordingItem.caseId}`
          : `${AudioDatabase.PREFIX_QUICK_RECORD}${Date.now()}`, // Use QuickRecord prefix for unassigned recordings
      );
      if (recordingItem.useTemplate) {
        try {
          mixpanel.track("Use Template", {});
        } catch (e) {
          // Mixpanel tracking error
        }
      }
      
      try {
        const createResult = await handleCreateRecording({
          name: recordingItem.name,
          duration: recordingItem.duration,
          storagePath: recordingItem.storagePath,
          useTemplate: recordingItem.useTemplate,
          caseId: recordingItem.caseId || undefined,
          templateId: recordingItem.templateId,
        });
        
        // Handle Supabase migration for both QuickRecords and Case Records
        if (createResult?.data?.createRecording) {
          const { fetchUserAttributes } = await import("aws-amplify/auth");
          const userInfo = await fetchUserAttributes();
          
          if (!recordingItem.caseId) {
            console.log('[UPLOAD] QuickRecord uploaded, migration will be handled by subscription when transcription arrives');
          } else {
            console.log('[UPLOAD] Case Record uploaded, attempting migration to Supabase');
          }
          
          // Try immediate sync for records with transcription
          setTimeout(async () => {
            try {
              const s3Recording = await pollForS3TriggerRecord(recordingItem, userInfo);
              
              if (s3Recording && s3Recording.text && s3Recording.text.length > 0) {
                console.log(`[UPLOAD] Found S3Trigger record with transcription, attempting immediate migration for ${recordingItem.caseId ? 'Case Record' : 'QuickRecord'}`);
                try {
                  const { DatabaseMigrationAdapter } = await import("@/services/migration");
                  const migrationAdapter = DatabaseMigrationAdapter.getInstance();
                  await migrationAdapter.handleRecordingOperation('CREATE', s3Recording as Record<string, unknown>, { data: { createRecording: s3Recording } } as unknown as Record<string, unknown>);
                  console.log('[UPLOAD] Immediate migration successful');
                } catch (syncError) {
                  console.log('[UPLOAD] Immediate migration failed, will be handled by subscription');
                }
              } else {
                console.log('[UPLOAD] No transcription yet, subscription will handle migration when ready');
              }
            } catch (pollingError) {
              console.log('[UPLOAD] Polling failed, subscription will handle migration');
            }
          }, 2000);
        }
      } catch (error) {
        // Failed to create recording in database
      }
      
      return true;
    } catch (error: unknown) {
      // Upload failed
      onSaveBlobToIndexDB(
        { ...recordingItem, failedToUpload: true },
        recordingItem.caseId 
          ? `${AudioDatabase.PREFIX_RECORD}${recordingItem.caseId}`
          : `${AudioDatabase.PREFIX_QUICK_RECORD}${Date.now()}`, // Use QuickRecord prefix for unassigned recordings
      );
      return false;
    }
  };
  return { handleUploadRecording };
};

export default useUploadRecording;
